import { useForm } from 'react-hook-form';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { TextArea } from 'src/ui/components/TextArea/TextArea';
import styles from './NewAppointment.module.scss';
import { Heading } from '../../ui/components/Heading/Heading';
import { Dropdown } from '../../ui/components/Dropdown/Dropdown';
import { useGQLLazyQuery, useGQLMutation } from '../../ui/utils/graphql';
import { GET_SERVICES } from '../../graphql/service';
import { InitialContext } from '../Layout/Layout';
import {
  AppointmentSlot,
  Client,
  Service,
  MutationCreateAppointmentArgs,
} from '../../gql/graphql';
import {
  CREATE_APPOINTMENT,
  GET_ALL_APPOINTMENTS_SLOTS_OF_DAY,
  GET_ALL_APPOINTMENTS_SLOTS_OF_MONTH,
} from '../../graphql/Appointments';
import { secondsToText, toLocalISOString } from '../../ui/utils/Date';
import { DatePicker } from '../../ui/components/DatePicker/DatePicker';
import { Button } from '../../ui/components/Button/Button';
import { GET_ALL_CLIENTS } from '../../graphql/client';
import { useToast } from '../../ui/components/Toast/ToastContainer';

interface NewAppointmentForm {
  service: string;
  subservice: string;
  date: string;
  time: string;
  client: string;
  initialComment?: string;
}

export const NewAppointment = () => {
  const { register, setValue, getValues, watch, handleSubmit } =
    useForm<NewAppointmentForm>({
      defaultValues: {
        initialComment: '',
      },
      mode: 'all',
      reValidateMode: 'onChange',
    });
  watch();
  const navigate = useNavigate();
  const { setToast } = useToast();
  const { company } = useContext(InitialContext);
  const [serviceSelected, setServiceSelected] = useState<Service>(null);
  const [subserviceSelected, setSubserviceSelected] = useState<Service>(null);
  const { data: services, lazy } = useGQLLazyQuery<
    Service[],
    { companyId: string }
  >(GET_SERVICES, {
    onCompleted: () => {},
  });
  const { data: slotsInAMonth, lazy: lazySlotsInAMonth } = useGQLLazyQuery<
    number[],
    { companyId: string; serviceId: string; month: string }
  >(GET_ALL_APPOINTMENTS_SLOTS_OF_MONTH, {
    onCompleted: () => {},
  });
  const { data: slotsInADay, lazy: lazySlotsInADay } = useGQLLazyQuery<
    AppointmentSlot[],
    { companyId: string; serviceId: string; day: string }
  >(GET_ALL_APPOINTMENTS_SLOTS_OF_DAY, {
    onCompleted: () => {},
  });
  const { data: allClients, lazy: lazyAllClients } = useGQLLazyQuery<
    Client[],
    { companyId: string }
  >(GET_ALL_CLIENTS, {
    onCompleted: () => {},
  });

  const { mutation: createAppointment } = useGQLMutation<
    string,
    MutationCreateAppointmentArgs
  >(CREATE_APPOINTMENT, {
    onCompleted: () => {
      navigate('/appointments');
      setToast({
        content: 'Cita creada correctamente',
        type: 'success',
      });
    },
  });

  useEffect(() => {
    console.log(getValues());
  }, [getValues()]);

  useEffect(() => {
    if (company?.id) {
      lazy({
        variables: {
          companyId: company.id,
        },
      });

      lazyAllClients({
        variables: {
          companyId: company.id,
        },
      });
    }
  }, [company]);

  useEffect(() => {
    if (
      (serviceSelected && serviceSelected.Childrens.length === 0) ||
      subserviceSelected
    ) {
      lazySlotsInAMonth({
        variables: {
          companyId: company.id,
          serviceId: subserviceSelected
            ? subserviceSelected?.id
            : serviceSelected?.id,
          month: new Date().toISOString().split('T')[0],
        },
      });
    }
    setValue('date', '//');
  }, [serviceSelected, subserviceSelected]);

  useEffect(() => {}, [getValues('date')]);

  const subservicePlaceholder = (): string => {
    if (!(getValues('service') === '' || !getValues('service'))) {
      return serviceSelected?.Childrens?.length > 0
        ? 'Selecciona un subservicio'
        : 'No hay subservicios disponibles para este servicio';
    }

    return 'Selecciona un servicio primero';
  };

  const renderServiceInformation = () => {
    if (serviceSelected && subserviceSelected) {
      return `El subservicio tiene una duración de ${secondsToText(
        Number(subserviceSelected.durationInSec)
      )}`;
    }

    return (
      serviceSelected &&
      serviceSelected.Childrens.length === 0 &&
      `El servicio tiene una duración de ${secondsToText(
        Number(serviceSelected.durationInSec)
      )}`
    );
  };

  useEffect(() => {
    setValue('time', '');
  }, [getValues('date')]);

  useEffect(() => {
    setValue('date', '//');
    setValue('time', '');
  }, [getValues('service'), getValues('subservice')]);

  const onSubmit = (data: NewAppointmentForm) => {
    const dayParsed = data.date.split('/');

    const day = dayParsed?.[0];
    const month = dayParsed?.[1];
    const year = dayParsed?.[2];
    const newDate = new Date(`${month}-${day}-${year}`);
    const newFormat = newDate.toISOString().split('T')?.[0];

    createAppointment({
      variables: {
        companyId: company?.id,
        dayOfAppointment: newFormat,
        hourOfAppointment: data.time,
        serviceId: data.subservice || data.service,
        clientId: data.client,
        initialComment: data.initialComment,
      },
    });
  };

  return (
    <div className={styles.root}>
      <Heading className={styles.heading} headingLevel="h1">
        Crea una nueva cita
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dropdown
          SetValue={setValue}
          label="Cliente"
          placeholder="Selecciona un cliente"
          name="client"
          register={register('client', {
            required: true,
          })}
          items={allClients?.map((client) => ({
            placeholder: `${client.name} ${client.lastName}`,
            value: client.id,
          }))}
        />
        <Dropdown
          SetValue={setValue}
          label="Servicio"
          placeholder="Selecciona un servicio"
          name="service"
          onChange={(data) => {
            setServiceSelected(services?.find((s) => s.id === data.value));
            setSubserviceSelected(null);
            setValue('subservice', '');
          }}
          register={register('service', {
            required: true,
          })}
          items={services?.map((service) => ({
            placeholder: service.name,
            value: service.id,
          }))}
        />
        <Dropdown
          SetValue={setValue}
          label="Subservicio"
          name="subservice"
          placeholder={subservicePlaceholder()}
          onChange={(data) => {
            setSubserviceSelected(
              serviceSelected?.Childrens?.find((s) => s.id === data.value)
            );
          }}
          readOnly={
            getValues('service') === '' ||
            !getValues('service') ||
            serviceSelected?.Childrens?.length === 0
          }
          register={register('subservice')}
          items={
            serviceSelected?.Childrens?.map((service) => ({
              placeholder: service.name,
              value: service.id,
            })) || []
          }
        />
        {renderServiceInformation() && (
          <div className={styles['service-information']}>
            {renderServiceInformation()}
          </div>
        )}
        {renderServiceInformation() && (
          <DatePicker
            register={register}
            firstDateAvailable={new Date(toLocalISOString(new Date()))}
            lastDateAvailable={(() => {
              const date = new Date();
              date.setDate(new Date().getDate() + 1200);
              return date;
            })()}
            label="Día de cita"
            defaultDate={new Date()}
            name="date"
            personalizeDays={slotsInAMonth?.map((percentaje) => {
              if (percentaje > 0.66 && percentaje <= 1) return 'success';
              if (percentaje > 0.33 && percentaje <= 0.66) return 'warning';
              if (percentaje > 0 && percentaje <= 0.33) return 'error';

              return 'disabled';
            })}
            onMonthChange={(month) => {
              lazySlotsInAMonth({
                variables: {
                  companyId: company.id,
                  serviceId: subserviceSelected
                    ? subserviceSelected?.id
                    : serviceSelected?.id,
                  month,
                },
              });
            }}
            onDayChange={(day) => {
              lazySlotsInADay({
                variables: {
                  companyId: company.id,
                  serviceId: subserviceSelected
                    ? subserviceSelected?.id
                    : serviceSelected?.id,
                  day,
                },
              });
            }}
            setValue={setValue}
          />
        )}
        {slotsInADay && getValues('date') !== '//' && (
          <Dropdown
            SetValue={setValue}
            name="time"
            label="Horario"
            placeholder="Selecciona el horario deseado"
            register={register('time', {
              required: {
                value: true,
                message: 'El tiempo es requerido',
              },
              minLength: {
                value: 4,
                message: 'El tiempo es requerido',
              },
            })}
            items={slotsInADay?.map((s) => ({
              value: s.startHour,
              placeholder: s.startHour,
            }))}
          />
        )}
        <TextArea
          label="Nota de cita (Opcional)"
          placeholder="Escribe una nota para la cita"
          name="initialComment"
          isRequired={false}
          errorMessage={null}
          register={register('initialComment', {
            required: false,
          })}
        />
        <Button className={styles.submit} type="submit">
          Create Appointment
        </Button>
      </form>
    </div>
  );
};
